import type { ColumnSizingState, Table } from "@tanstack/react-table";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { MIN_COLUMN_WIDTH } from "./constants";

interface UseColumnSizingOptions<T> {
  tableWidth: number | null;
  columnVisibility: Record<string, boolean>;
  visibleColumnIds: string[];
  getTable: () => Table<T> | null;
  horizontalOverflow?: boolean;
}

interface UseColumnSizingReturn {
  columnSizing: ColumnSizingState;
  handleColumnSizingChange: (
    updaterOrValue: ColumnSizingState | ((old: ColumnSizingState) => ColumnSizingState),
  ) => void;
}

/**
 * Manages column sizing state for DataTable.
 * Handles:
 * - Initial distribution of widths based on preset widths and tableWidth
 * - Adjacent column transfer during resize operations
 * - Minimum width enforcement
 */
export function useColumnSizing<T>({
  tableWidth,
  columnVisibility,
  visibleColumnIds,
  getTable,
  horizontalOverflow,
}: UseColumnSizingOptions<T>): UseColumnSizingReturn {
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const prevColumnSizingRef = useRef<ColumnSizingState>({});

  const handleColumnSizingChange = useCallback(
    (updaterOrValue: ColumnSizingState | ((old: ColumnSizingState) => ColumnSizingState)) => {
      const prevSizing = prevColumnSizingRef.current;

      // Don't process sizing changes until useLayoutEffect has established initial sizing
      if (Object.keys(prevSizing).length === 0) {
        return;
      }

      const newSizing = typeof updaterOrValue === "function" ? updaterOrValue(columnSizing) : { ...updaterOrValue };
      const table = getTable();

      const getColSize = (colId: string) => prevSizing[colId] ?? table?.getColumn(colId)?.getSize() ?? 0;

      // Find which column was resized and calculate the delta
      let resizedColId: string | undefined;
      let delta = 0;
      for (const colId of Object.keys(newSizing)) {
        const newSize = newSizing[colId];
        const prevSize = getColSize(colId);
        if (newSize !== prevSize && newSize !== undefined) {
          resizedColId = colId;
          delta = newSize - prevSize;
          break;
        }
      }

      if (resizedColId && delta !== 0 && table) {
        if (horizontalOverflow) {
          // Horizontal overflow mode: resize only the dragged column, changing total table width
          const currentSize = getColSize(resizedColId);
          if (delta > 0) {
            newSizing[resizedColId] = currentSize + delta;
          } else {
            const minAllowedDelta = MIN_COLUMN_WIDTH - currentSize;
            const cappedDelta = Math.max(delta, minAllowedDelta);
            if (cappedDelta >= 0) {
              return;
            }
            newSizing[resizedColId] = currentSize + cappedDelta;
          }
        } else {
          // Fixed-width mode: transfer delta to adjacent column to preserve total width
          const colIndex = visibleColumnIds.indexOf(resizedColId);
          const nextColId = visibleColumnIds[colIndex + 1];

          if (delta > 0) {
            if (!nextColId) {
              return;
            }
            const nextColCurrentSize = getColSize(nextColId);
            const availableSpace = nextColCurrentSize - MIN_COLUMN_WIDTH;
            if (availableSpace <= 0) {
              return;
            }
            const cappedDelta = Math.min(delta, availableSpace);
            newSizing[resizedColId] = getColSize(resizedColId) + cappedDelta;
            newSizing[nextColId] = nextColCurrentSize - cappedDelta;
          } else {
            const currentSize = getColSize(resizedColId);
            const minAllowedDelta = MIN_COLUMN_WIDTH - currentSize;
            const cappedDelta = Math.max(delta, minAllowedDelta);
            if (cappedDelta >= 0) {
              return;
            }
            newSizing[resizedColId] = currentSize + cappedDelta;
            if (nextColId) {
              newSizing[nextColId] = getColSize(nextColId) - cappedDelta;
            }
          }
        }
      }

      prevColumnSizingRef.current = newSizing;
      setColumnSizing(newSizing);
    },
    [columnSizing, visibleColumnIds, getTable, horizontalOverflow],
  );

  // Store getTable in a ref to avoid re-running effect when function reference changes
  const getTableRef = useRef(getTable);
  getTableRef.current = getTable;

  // Serialize columnVisibility for stable dependency comparison
  const columnVisibilityKey = JSON.stringify(columnVisibility);

  useLayoutEffect(() => {
    const table = getTableRef.current();
    const visibleCols = table?.getVisibleLeafColumns();
    if (!tableWidth || !visibleCols?.length) {
      return;
    }

    const newSizing: ColumnSizingState = {};

    // Collect preset widths (from width prop) and columns needing distribution
    let presetTotal = 0;
    const colsNeedingDistribution: typeof visibleCols = [];

    for (const col of visibleCols) {
      const presetSize = col.columnDef.size;
      if (presetSize !== undefined) {
        newSizing[col.id] = presetSize;
        presetTotal += presetSize;
      } else {
        colsNeedingDistribution.push(col);
      }
    }

    // Distribute remaining width equally to columns without presets
    if (colsNeedingDistribution.length > 0) {
      const remaining = tableWidth - presetTotal;
      const widthPerCol = Math.max(MIN_COLUMN_WIDTH, remaining / colsNeedingDistribution.length);
      for (const col of colsNeedingDistribution) {
        newSizing[col.id] = widthPerCol;
      }
    }

    // If total doesn't match tableWidth, distribute difference equally among ALL columns.
    // In horizontal overflow mode, only equalize when columns under-fill the table width
    // (don't shrink columns that the user has expanded beyond the container).
    const total = Object.values(newSizing).reduce((sum, size) => sum + size, 0);
    if (Math.abs(total - tableWidth) > 1 && (!horizontalOverflow || total < tableWidth)) {
      const extraPerCol = (tableWidth - total) / visibleCols.length;
      for (const col of visibleCols) {
        newSizing[col.id] = Math.max(MIN_COLUMN_WIDTH, (newSizing[col.id] ?? 0) + extraPerCol);
      }
    }

    // Only update if sizing actually changed
    const sizingChanged = visibleCols.some((col) => {
      const newSize = newSizing[col.id];
      const oldSize = columnSizing[col.id];
      return oldSize === undefined || Math.abs(newSize - oldSize) > 1;
    });

    if (sizingChanged) {
      prevColumnSizingRef.current = newSizing;
      setColumnSizing(newSizing);
    }
  }, [tableWidth, columnVisibilityKey]);

  return {
    columnSizing,
    handleColumnSizingChange,
  };
}
